import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';

import { ShopifyStockControlPage } from '@pages/shopify-management';
import type { RouterContext } from '@shared/config/router-context';
import { canAccessAnalyticsGear } from '@shared/lib';
import { useAbility } from '@features/authorization';
import { useAuth } from '@features/auth';
import { Header } from '@widgets/header';
import { proposalReviewSearchSchema } from '@features/shopify-stock-control';

export const Route = createFileRoute('/$locale/_authenticated/shopify/stock-control')({
  validateSearch: proposalReviewSearchSchema,
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessAnalyticsGear(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: ShopifyStockControlRoute,
});

function ShopifyStockControlRoute() {
  const { locale } = Route.useParams();
  const ability = useAbility();
  const auth = useAuth();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated || canAccessAnalyticsGear(ability)) return;
    void navigate({ to: '/$locale/unauthorized', params: { locale }, replace: true });
  }, [ability, auth.isAuthenticated, auth.isLoading, locale, navigate]);

  if (auth.isLoading || !auth.isAuthenticated || !canAccessAnalyticsGear(ability)) return null;

  return (
    <div className="flex min-h-full flex-col pt-16 min-[992px]:pt-0">
      <Header locale={locale} className="min-[992px]:hidden" />
      <main className="min-w-0 p-6">
        <ShopifyStockControlPage
          reviewSearch={search}
          onReviewSearchChange={(patch) => {
            void navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true });
          }}
        />
      </main>
    </div>
  );
}
